import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InputNumber, Button } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import { restrictToTwoDecimals } from "../../utils";

interface DensityInput {
  id: string;
  value: number | null;
}

interface DynamicDensityInputsProps {
  productId: string;
  productName?: string;

  onDensityValuesChange: (productId: string, values: number[]) => void;
  onDensityValuesBlur?: (productId: string, values: number[]) => void;
  initialValues?: number[];
}

const valuesFromInputs = (inputs: DensityInput[]): number[] =>
  inputs.map((i) => i.value).filter((v): v is number => typeof v === "number");

const normalizeValues = (arr: number[]): number[] =>
  Array.from(new Set(arr.filter((n) => Number.isFinite(n)))).sort(
    (a, b) => a - b
  );

const sameNumberArray = (a: number[], b: number[]): boolean =>
  a.length === b.length && a.every((v, idx) => v === b[idx]);

export const DynamicDensityInputs = ({
  productId,
  productName,
  onDensityValuesChange,
  onDensityValuesBlur,
  initialValues = [],
}: DynamicDensityInputsProps): React.ReactElement => {
  const idPrefix = useMemo(() => `density-${productId}`, [productId]);

  const makeInputsFrom = useCallback(
    (values: number[]): DensityInput[] => {
      const normalized = normalizeValues(values);

      const filled: DensityInput[] = normalized.map((val, idx) => ({
        id: `${idPrefix}-${idx}`,
        value: val,
      }));

      const emptyRow: DensityInput = {
        id: `${idPrefix}-${normalized.length}`,
        value: null,
      };

      return [...filled, emptyRow];
    },
    [idPrefix]
  );

  const [inputs, setInputs] = useState<DensityInput[]>(() =>
    makeInputsFrom(initialValues)
  );

  const lastSyncedValuesRef = useRef<number[]>(normalizeValues(initialValues));
  const isFocusWithinRef = useRef(false);

  useEffect(() => {
    const incomingNormalized = normalizeValues(initialValues);

    if (sameNumberArray(incomingNormalized, lastSyncedValuesRef.current))
      return;
    if (isFocusWithinRef.current) return;

    lastSyncedValuesRef.current = incomingNormalized;
    setInputs(makeInputsFrom(incomingNormalized));
  }, [initialValues, makeInputsFrom]);

  const emitChange = (values: number[]): void => {
    onDensityValuesChange(productId, values);
  };

  const rebuildFromSorted = (): void => {
    const current = valuesFromInputs(inputs);
    const normalized = normalizeValues(current);

    lastSyncedValuesRef.current = normalized;
    setInputs(makeInputsFrom(normalized));

    if (onDensityValuesBlur) onDensityValuesBlur(productId, current);
  };

  const handleRemoveById = (idToRemove: string): void => {
    setInputs((prev) => {
      const next = prev.filter((row) => row.id !== idToRemove);

      const hasEmptyAtEnd =
        next.length > 0 && next[next.length - 1].value === null;
      if (!hasEmptyAtEnd) {
        next.push({ id: `${idPrefix}-${next.length}`, value: null });
      }

      emitChange(valuesFromInputs(next));
      return next;
    });
  };

  const handleValueChange = (id: string, value: number | null): void => {
    setInputs((prev) => {
      const next = prev.map((row) => (row.id === id ? { ...row, value } : row));

      const hasEmptyAtEnd =
        next.length > 0 && next[next.length - 1].value === null;
      if (!hasEmptyAtEnd) {
        next.push({ id: `${idPrefix}-${next.length}`, value: null });
      }

      emitChange(valuesFromInputs(next));
      return next;
    });
  };

  const handleAddInput = (): void => {
    setInputs((prev) => {
      const hasEmptyAtEnd =
        prev.length > 0 && prev[prev.length - 1].value === null;
      if (hasEmptyAtEnd) return prev;

      return [...prev, { id: `${idPrefix}-${prev.length}`, value: null }];
    });
  };

  return (
    <div
      className="dynamic-density-inputs"
      onFocusCapture={() => {
        isFocusWithinRef.current = true;
      }}
      onBlurCapture={(e) => {
        const nextFocused = e.relatedTarget as Node | null;
        const stillInside =
          nextFocused !== null && e.currentTarget.contains(nextFocused);

        if (!stillInside) {
          isFocusWithinRef.current = false;
          rebuildFromSorted();
        }
      }}
    >
      {productName && (
        <>
          <div className="density-product-name">{productName}</div>
          <div className="density-product-divider" />
        </>
      )}

      <div className="density-inputs-container">
        {inputs.map((input, index) => {
          const isLastRow = index === inputs.length - 1;

          return (
            <div key={input.id} className="density-input-row">
              <div className="density-input-wrapper">
                <div className="density-input-inner">
                  <InputNumber
                    placeholder="0"
                    min={0}
                    max={999999}
                    step={0.01}
                    precision={2}
                    value={input.value}
                    className="density-input-field"
                    size="small"
                    controls={false}
                    onChange={(val) => {
                      handleValueChange(
                        input.id,
                        typeof val === "number" ? val : null
                      );
                    }}
                    onKeyDown={restrictToTwoDecimals}
                  />

                  {!isLastRow && (
                    <button
                      type="button"
                      className="density-input-clear"
                      aria-label="Remove density option"
                      title="Remove density option"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => {
                        handleRemoveById(input.id);
                      }}
                    >
                      <CloseOutlined />
                    </button>
                  )}
                </div>

                {isLastRow && (
                  <Button
                    type="text"
                    size="small"
                    icon={<PlusOutlined style={{ fontSize: "12px" }} />}
                    aria-label="Add density option"
                    title="Add density option"
                    className="density-action-btn add-btn"
                    onClick={handleAddInput}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
